import { Icon } from "./Icon.js";

export interface VideoControlOptions {
    container: HTMLElement;
    visible?: boolean;
    video: HTMLVideoElement;
}

export class VideoControl {

    private _element: HTMLElement;
    private _video: HTMLVideoElement;
    private _state: "playing" | "paused" = "playing";


    constructor(options: VideoControlOptions) {
        options = e5.object.merge({
            visible: true
        }, options);

        this._element = document.createElement("div");
        this._element.className = "video-control";
        this._element.innerHTML = `
        
        <div class="video-control__progress">
            <div class="video-control__progress-track video-control__progress-track--start"></div>
            <div class="video-control__progress-track video-control__progress-track--end"></div>
            <div class="video-control__progress-thumb"></div>
        </div>
        <div class="video-control__button-wrapper" data-state="${this._state}">
            <div class="video-control__button video-control__button--playing"><span class="${Icon.VIDEO_STATE_PLAYING}"></span></div>
            <div class="video-control__button video-control__button--paused"><span class="${Icon.VIDEO_STATE_PAUSED}"></span></div>
        </div>
        <div class="video-control__time"></div>
        `;

        this._video = options.video;


        let progressBar = this._element.querySelector(".video-control__progress") as HTMLElement;

        var actor = new e5.Actor(
            progressBar,
            {
                preventDefault: true
            });

        var updateProgressPosition = (): void => {
            const local = e5.Transform.toLocal(progressBar, actor.pointer.x, actor.pointer.y);
            this.setTime((local.x / progressBar.offsetWidth) * this._video.duration);
        };
        actor.onStart.add(() => {
            progressBar.classList.add("video-control__progress--dragging");
        });
        actor.onClear.add(() => {
            progressBar.classList.remove("video-control__progress--dragging");
        });
        actor.onDown.add(updateProgressPosition);
        actor.onMove.add(updateProgressPosition);
        actor.onUp.add(() => {
            var local = e5.Transform.toLocal(progressBar, actor.pointer.x, actor.pointer.y);
            var time = e5.Calc.clamp(local.x / progressBar.offsetWidth, 0, 1) * this._video.duration;
            if (isFinite(time)) {
                this._video.currentTime = time;
            }
        });

        this._video.addEventListener("timeupdate", () => {
            if (actor.pointers.length > 0) {
                return;
            }
            this.setTime(this._video.currentTime);
        });

        this._video.addEventListener("loadstart", (e) => {
            if (actor.pointers.length > 0) {
                return;
            }
            this.setTime(0);
        });

        this._video.addEventListener("pause", () => this.state = "paused");
        this._video.addEventListener("playing", () => this.state = "playing");

        this._element.querySelector(".video-control__button-wrapper").addEventListener("click", () => {
            if (this._video.paused) {
                this._video.play();
            } else {
                this._video.pause();
            }
        });

        if (!options.visible) {
            this.visible = false;
        }

        if (options.container) {
            options.container.appendChild(this._element);
        }
    }

    public get state(): "playing" | "paused" {
        return this._state;
    }

    public set state(value: "playing" | "paused") {
        this._state = value;
        this._element.querySelector(".video-control__button-wrapper").setAttribute("data-state", value);
    }

    private setTime(time: number): void {
        time = e5.Calc.clamp(time, 0, this._video.duration);

        let ratio: number = this._video.duration > 0 ? e5.Calc.clamp(time / this._video.duration, 0, 1) : 0;

        let thumb = this._element.querySelector(".video-control__progress-thumb") as HTMLElement;
        thumb.style.left = (e5.Calc.clamp(ratio, 0, 1) * 100).toFixed(4) + "%";

        let start = this._element.querySelector(".video-control__progress-track--start") as HTMLElement;
        start.style.clipPath = `inset(0 ${(e5.Calc.clamp(1 - ratio, 0, 1) * 100).toFixed(4) + "%"} 0 0)`;

        let end = this._element.querySelector(".video-control__progress-track--end") as HTMLElement;
        end.style.clipPath = `inset(0 0 0 ${(e5.Calc.clamp(ratio, 0, 1) * 100).toFixed(4) + "%"})`;

        let t = this._element.querySelector(".video-control__time");

        if(this._video.duration){
            t.classList.remove("video-control__time--hidden");
            t.innerHTML = `<span class="video-control__time--current">${this.formatTime(time)}</span> | <span class="video-control__time--total">${this.formatTime(this._video.duration)}</span>`;
        } else {
            t.classList.add("video-control__time--hidden");
            t.innerHTML = "";
        }
    }

    public update():void{
        this.setTime(this._video.currentTime);
    }

    private formatTime(time: number): string {
        time = Math.round(time);
        if (time <= 0) {
            return "0:00";
        }

        let seconds = time % 60;
        let minutes = Math.floor(time / 60);
        let hours = Math.floor(time / (60 * 60));

        let parts: string[] = [];
        if (hours > 0) {
            parts.push(hours + "");
        }
        parts.push(minutes + "");
        parts.push(e5.Text.fillHead(seconds + "", 2, "0"));

        return parts.join(":");
    }

    public get visible(): boolean {
        return !this._element.classList.contains("video-control--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }

        if (value) {
            this._element.classList.toggle("video-control--hidden", !value);
            this.setTime(0);
        } else {
            this.setTime(0);
            this._element.classList.toggle("video-control--hidden", !value);
        }
    }
}